import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { pdfBrandLogo } from '../../pdf-brand.js';
import { TOKENS } from '../../tokens.js';
import { formatMoney, formatQuantity, formatUnit } from '../materials/inventory.js';
import { monthLabel } from '../payroll/view.js';
import type { VictuallingData, VictuallingFleetData } from './types.js';
import { amountIn, FLOW_COLUMNS, moneyText, signedPercent } from './victualling-view.js';

/** Çevirici: i18next `t`. */
export type VictuallingT = (key: string, options?: Record<string, unknown>) => string;

type Brand = { name: string; logo: string | null } | null;

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

function header(t: VictuallingT, brand: Brand, subject: string, printedAt: string): Content {
  const logo = pdfBrandLogo(brand, { fit: [80, 40], width: 90 });
  return {
    columns: [
      ...(logo ? [logo] : []),
      {
        stack: [
          { text: brand?.name ?? '', bold: true, fontSize: 11 },
          { text: subject, fontSize: 10 },
        ],
      },
      {
        text: t('victualling.pdf.printed', { at: printedAt.slice(0, 16).replace('T', ' ') }),
        alignment: 'right',
        fontSize: 8,
        color: TOKENS['text-secondary'],
      },
    ],
  };
}

const right = (text: string) => ({ text, alignment: 'right' as const });
const head = (labels: string[], numericFrom: number): TableCell[] =>
  labels.map((text, i) => ({
    text,
    bold: true,
    alignment: i >= numericFrom ? ('right' as const) : ('left' as const),
  }));

function document(title: string, content: Content[]): TDocumentDefinitions {
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [32, 32, 32, 44],
    defaultStyle: { fontSize: 8 },
    info: { title },
    content,
    footer: (page, pages) => ({
      text: `Felixmate · ${page} / ${pages}`,
      alignment: 'center',
      fontSize: 7,
      color: TOKENS['text-secondary'],
    }),
  };
}

/**
 * Geminin aylık iaşe raporu (ADR 0123): A4 yatay; özet (kişi-gün, POB, para birimi başına oran ve
 * bütçe), her para birimi için kategori akışı, maliyet merkezleri, kalemler ve imza kutuları
 * (kumanya sorumlusu birinci zabit, kaptan, ofis; ADR 0139). Para birimleri toplanmaz.
 */
export function victuallingPdf(
  t: VictuallingT,
  input: { report: VictuallingData; brand: Brand; printedAt: string; language: string },
): TDocumentDefinitions {
  const { report, language } = input;
  const v = (key: string, options?: Record<string, unknown>) => t(`victualling.${key}`, options);
  const money = (amount: number | null, currency: string) =>
    amount === null ? '—' : formatMoney(amount, currency, language);
  const qty = (value: number) => formatQuantity(value, language);
  const title = v('pdf.title', { month: monthLabel(report.month, language) });
  const flowLabels = FLOW_COLUMNS.map((c) => v(`categories.${c}`));

  const summary: Content = {
    table: {
      widths: [140, '*'],
      body: [
        [v('vessel'), { text: report.vessel.name, bold: true }],
        [
          v('period'),
          `${formatDay(report.periodStart)} – ${formatDay(report.periodEnd)}${
            report.complete ? '' : ` (${v('pdf.partial')})`
          }`,
        ],
        [v('cards.personDays'), qty(report.personDays)],
        [v('cards.averagePob'), qty(report.averagePob)],
      ],
    },
    layout: { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 },
    margin: [0, 0, 0, 10],
  };

  const rates: Content =
    report.rates.length === 0
      ? { text: v('rates.none'), italics: true, margin: [0, 0, 0, 10] }
      : {
          table: {
            headerRows: 1,
            widths: ['*', 90, 90, 90, 90, 70],
            body: [
              head(
                [
                  v('rates.currency'),
                  v('rates.consumption'),
                  v('rates.rate'),
                  v('rates.budgetRate'),
                  v('rates.variance'),
                  '%',
                ],
                1,
              ),
              ...report.rates.map((r) => [
                r.currency,
                right(money(r.consumption, r.currency)),
                right(r.rate === null ? '—' : money(r.rate, r.currency)),
                right(r.budget ? money(r.budget.dailyRate, r.currency) : v('rates.noBudget')),
                right(r.budget ? money(r.budget.variance, r.currency) : '—'),
                right(signedPercent(r.budget?.variancePct ?? null, language)),
              ]),
            ],
          },
          layout: LINES,
          margin: [0, 0, 0, 4],
        };

  const categoryTables: Content[] = report.currencies.map((currency) => ({
    stack: [
      {
        text: `${v('categories.title')} (${currency})`,
        bold: true,
        fontSize: 10,
        margin: [0, 8, 0, 4],
      },
      {
        table: {
          headerRows: 1,
          widths: ['*', 36, 64, 64, 64, 64, 64, 64],
          body: [
            head([v('categories.category'), v('categories.items'), ...flowLabels], 1),
            ...report.categories.map((c) => [
              `${c.categoryCode ? `${c.categoryCode} · ` : ''}${c.mainCategory}${
                c.unpricedItems > 0
                  ? ` (${v('categories.unpriced', { count: c.unpricedItems })})`
                  : ''
              }`,
              right(qty(c.items)),
              ...FLOW_COLUMNS.map((f) =>
                right(money(amountIn(c.value[f], currency) ?? 0, currency)),
              ),
            ]),
            [
              { text: v('categories.total'), bold: true },
              right(qty(report.items.length)),
              ...FLOW_COLUMNS.map((f) => ({
                ...right(money(amountIn(report.totals[f], currency) ?? 0, currency)),
                bold: true,
              })),
            ],
          ],
        },
        layout: LINES,
      },
    ],
  }));

  const centers: Content = {
    stack: [
      { text: v('costCenters.title'), bold: true, fontSize: 10, margin: [0, 10, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 180, 140, 60],
          body: [
            head(
              [
                v('costCenters.costCenter'),
                v('costCenters.consumption'),
                v('costCenters.disposal'),
                v('costCenters.inRate'),
              ],
              1,
            ),
            ...report.costCenters.map((c) => [
              c.costCenter ?? v('costCenters.none'),
              right(moneyText(c.consumption, language)),
              right(moneyText(c.disposal, language)),
              right(c.inRateBasis ? v('costCenters.yes') : v('costCenters.no')),
            ]),
          ],
        },
        layout: LINES,
      },
    ],
  };

  const items: Content = {
    stack: [
      { text: v('items.title'), bold: true, fontSize: 10, margin: [0, 10, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 34, 46, 46, 50, 46, 46, 46, 110],
          body: [
            head([v('items.item'), v('items.unit'), ...flowLabels, v('items.valueAny')], 2),
            ...report.items.map((i) => [
              `${i.name}${i.itemCode ? ` · ${i.itemCode}` : ''}${
                i.unpriced ? ` (${v('items.unpriced')})` : ''
              }${i.inRateBasis ? '' : ` (${v('items.notInRate')})`}`,
              formatUnit(i.unit),
              ...FLOW_COLUMNS.map((f) => right(qty(i.quantity[f]))),
              right(moneyText(i.value.consumption, language)),
            ]),
          ],
        },
        layout: LINES,
      },
    ],
  };

  const signature = (label: string) => ({
    stack: [
      { text: label, bold: true },
      { canvas: [{ type: 'line' as const, x1: 0, y1: 28, x2: 200, y2: 28, lineWidth: 0.5 }] },
    ],
  });

  return document(title, [
    header(t, input.brand, report.vessel.name, input.printedAt),
    { text: title, bold: true, fontSize: 14, margin: [0, 12, 0, 8] },
    summary,
    rates,
    { text: v('rates.basis'), fontSize: 7, color: TOKENS['text-secondary'], margin: [0, 0, 0, 4] },
    ...categoryTables,
    {
      text: v('categories.equation'),
      fontSize: 7,
      color: TOKENS['text-secondary'],
      margin: [0, 2, 0, 0],
    },
    centers,
    items,
    {
      columns: [
        // Kumanya sorumlusu birinci zabit (ADR 0139 §1)
        signature(v('pdf.chiefOfficer')),
        signature(v('pdf.master')),
        signature(v('pdf.office')),
      ],
      margin: [0, 24, 0, 0],
    },
  ]);
}

/** Filo görünümünün PDF'i (ADR 0123 §6): gemi × para birimi satırları ve filo toplamları. */
export function victuallingFleetPdf(
  t: VictuallingT,
  input: { fleet: VictuallingFleetData; brand: Brand; printedAt: string; language: string },
): TDocumentDefinitions {
  const { fleet, language } = input;
  const v = (key: string, options?: Record<string, unknown>) => t(`victualling.${key}`, options);
  const money = (amount: number | null | undefined, currency: string) =>
    amount === null || amount === undefined ? '—' : formatMoney(amount, currency, language);
  const qty = (value: number) => formatQuantity(value, language);
  const title = v('pdf.fleetTitle', { month: monthLabel(fleet.month, language) });
  const body: TableCell[][] = [
    head(
      [
        v('fleetView.vessel'),
        v('cards.personDays'),
        v('cards.averagePob'),
        v('rates.currency'),
        v('rates.consumption'),
        v('rates.rate'),
        v('rates.budgetRate'),
        v('rates.variance'),
        '%',
      ],
      1,
    ),
  ];
  for (const row of fleet.rows) {
    const rates = row.rates.length > 0 ? row.rates : [null];
    rates.forEach((rate, i) => {
      body.push([
        i === 0 ? row.name : '',
        right(i === 0 ? qty(row.personDays) : ''),
        right(i === 0 ? qty(row.averagePob) : ''),
        rate?.currency ?? v('fleetView.noConsumption'),
        right(rate ? money(rate.consumption, rate.currency) : '—'),
        right(rate ? money(rate.rate, rate.currency) : '—'),
        right(rate?.budget ? money(rate.budget.dailyRate, rate.currency) : '—'),
        right(rate?.budget ? money(rate.budget.variance, rate.currency) : '—'),
        right(signedPercent(rate?.budget?.variancePct ?? null, language)),
      ]);
    });
  }
  for (const total of fleet.totals) {
    body.push([
      { text: v('fleetView.total', { count: total.vessels }), bold: true },
      right(qty(total.personDays)),
      '',
      total.currency,
      right(money(total.consumption, total.currency)),
      right(money(total.rate, total.currency)),
      '',
      '',
      '',
    ]);
  }
  return document(title, [
    header(t, input.brand, v('fleet'), input.printedAt),
    { text: title, bold: true, fontSize: 14, margin: [0, 12, 0, 8] },
    {
      table: { headerRows: 1, widths: ['*', 56, 56, 50, 90, 80, 70, 80, 50], body },
      layout: LINES,
    },
    { text: v('rates.basis'), fontSize: 7, color: TOKENS['text-secondary'], margin: [0, 6, 0, 0] },
  ]);
}
